import React, { useState } from 'react'; 
import {
  Box,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Paper,
  Stack,
  Modal,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import MappingIcon from '@mui/icons-material/SwapHoriz';
import axios from 'axios';
import Papa from 'papaparse';
import { parse, format, isValid } from 'date-fns';

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('idToken') || localStorage.getItem('token');
};

// Create axios instance with auth header
const createAuthenticatedRequest = () => {
  const token = getAuthToken();
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const CSVUploadPage = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewHeaders, setPreviewHeaders] = useState([]);
  const [duplicateHeaders, setDuplicateHeaders] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [fileStats, setFileStats] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [columnMapping, setColumnMapping] = useState({});
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [strategies, setStrategies] = useState([]);
  
  const theme = useTheme();

  const apiUrl = 'https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/journalapp_upload_csv';

  // Fetch strategies on component mount
  React.useEffect(() => {
    fetchStrategies();
  }, []);

  const fetchStrategies = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStrategies(response.data);
    } catch (err) {
      console.error('Error fetching strategies:', err);
    }
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const REQUIRED_COLUMNS = ['symbol', 'entryPrice', 'exitPrice', 'tradeDate'];
  const OPTIONAL_COLUMNS = ['quantity', 'direction', 'stopLoss', 'notes', 'strategyIds'];
  
  const COLUMN_MAPPINGS = {
    symbol: ['symbol', 'ticker', 'instrument', 'asset', 'pair'],
    entryPrice: ['entryprice', 'entry_price', 'buy_price', 'open_price', 'entry'],
    exitPrice: ['exitprice', 'exit_price', 'sell_price', 'close_price', 'exit'],
    tradeDate: ['tradedate', 'trade_date', 'date', 'open_date', 'entry_date'],
    quantity: ['quantity', 'size', 'amount', 'volume', 'shares'],
    direction: ['direction', 'side', 'type', 'action', 'buy_sell'],
    stopLoss: ['stoploss', 'stop_loss', 'sl', 'stop'],
    notes: ['notes', 'comment', 'description', 'memo']
  };

  const sanitizeHeaders = (headers) => {
    const seen = new Set();
    return headers.map((h) => {
      let clean = h.toString().trim().replace(/[$%]/g, '').replace(/\s+/g, ' ');
      let unique = clean;
      let i = 1;
      while (seen.has(unique)) {
        unique = `${clean}_${i++}`;
      }
      seen.add(unique);
      return unique;
    });
  };

  const validateFile = (file) => {
    const errors = [];
    
    if (!file) {
      errors.push('No file selected');
      return { isValid: false, errors };
    }
    
    if (file.size > MAX_FILE_SIZE) {
      errors.push(`File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds limit of 10MB`);
    }
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      errors.push('File must be a CSV format');
    }
    
    return { isValid: errors.length === 0, errors };
  };

  const validateMapping = () => {
    const missingRequired = REQUIRED_COLUMNS.filter(col => !columnMapping[col]);
    return { isValid: missingRequired.length === 0, missingRequired };
  };

  const validateData = (data, headers) => {
    const results = {
      totalRows: data.length,
      validRows: 0,
      errors: [],
      warnings: [],
      columnStats: {}
    };

    // Check required columns with mapping
    const missingColumns = REQUIRED_COLUMNS.filter(col => {
      const mappedCol = columnMapping[col];
      return !mappedCol || !headers.includes(mappedCol);
    });
    
    if (missingColumns.length > 0) {
      results.errors.push(`Missing required columns: ${missingColumns.join(', ')}`);
    }

    // Validate each row
    data.forEach((row, index) => {
      let rowValid = true;
      
      // Check for empty required fields with mapping
      REQUIRED_COLUMNS.forEach(col => {
        const mappedCol = columnMapping[col];
        if (mappedCol && (!row[mappedCol] || row[mappedCol].toString().trim() === '')) {
          results.warnings.push(`Row ${index + 1}: Missing ${col}`);
          rowValid = false;
        }
      });

      // Validate numeric fields with mapping
      ['entryPrice', 'exitPrice', 'quantity'].forEach(field => {
        const mappedCol = columnMapping[field];
        if (mappedCol && row[mappedCol]) {
          const value = parseFloat(row[mappedCol]);
          if (isNaN(value) || value <= 0) {
            results.warnings.push(`Row ${index + 1}: Invalid ${field} value`);
            rowValid = false;
          }
        }
      });

      if (rowValid) results.validRows++;
    });

    // Column statistics
    headers.forEach(header => {
      const nonEmptyValues = data.filter(row => row[header] && row[header].toString().trim() !== '').length;
      results.columnStats[header] = {
        fillRate: ((nonEmptyValues / data.length) * 100).toFixed(1),
        emptyCount: data.length - nonEmptyValues
      };
    });

    return results;
  };

  const normalizeDate = (value) => {
    if (typeof value !== 'string') return value;

    const formatsToTry = [
        'dd.MM.yyyy',
        'dd.MM.yyyy HH:mm',
        'dd/MM/yyyy',
        'MM/dd/yyyy',
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm',
    ];

    for (const formatStr of formatsToTry) {
        const parsed = parse(value, formatStr, new Date());
        if (isValid(parsed)) {
        return format(parsed, 'yyyy-MM-dd');
        }
    }

    return value;
  };


  const convertToSanitizedCSV = (rows, originalHeaders) => {
    const mappedData = rows.map(row => {
      const mappedRow = {};
      [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS].forEach(field => {
        const mappedCol = columnMapping[field];
        if (mappedCol && row[mappedCol] !== undefined) {
          let value = row[mappedCol];
          if (typeof value === 'string') {
            value = value.trim();
            if (field === 'tradeDate') value = normalizeDate(value);
          }
          mappedRow[field] = value;
        }
      });
      // Add selected strategy if one is chosen
      if (selectedStrategy) {
        mappedRow.strategyIds = `["${selectedStrategy}"]`;
      }
      return mappedRow;
    });
    return Papa.unparse(mappedData);
  };

  const handleFileSelect = (selectedFile) => {
    const validation = validateFile(selectedFile);
    
    if (!validation.isValid) {
      setError(validation.errors.join(', '));
      return;
    }
    
    setFile(selectedFile);
    setMessage(null);
    setError(null);
    setValidationResults(null);
    
    // Set file stats
    setFileStats({
      name: selectedFile.name,
      size: (selectedFile.size / 1024).toFixed(2) + ' KB',
      lastModified: new Date(selectedFile.lastModified).toLocaleDateString()
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleUpload = async () => {
    const validation = validateFile(file);
    if (!validation.isValid) {
      setError(validation.errors.join(', '));
      return;
    }

    setUploading(true);
    setMessage(null);
    setError(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      complete: async (results) => {
        const headers = results.meta.fields;
        const seen = new Set();
        const duplicates = headers.filter((h) => {
          if (seen.has(h)) return true;
          seen.add(h);
          return false;
        });

        if (duplicates.length > 0) {
          setDuplicateHeaders(duplicates);
          setShowDuplicateModal(true);
        }

        setCsvHeaders(headers);
        setPreviewHeaders(headers.slice(0, 10));
        setPreviewRows(results.data.slice(0, 5));
        
        // Auto-detect column mappings
        const autoMapping = {};
        Object.entries(COLUMN_MAPPINGS).forEach(([standardCol, variations]) => {
          const matchedHeader = headers.find(h => 
            variations.some(v => h.toLowerCase().includes(v.toLowerCase()))
          );
          if (matchedHeader) {
            autoMapping[standardCol] = matchedHeader;
          }
        });
        setColumnMapping(autoMapping);
        
        // Validate data
        const validation = validateData(results.data, headers);
        setValidationResults(validation);
        
        if (validation.errors.length > 0 || !validateMapping().isValid) {
          setShowMappingModal(true);
          setUploading(false);
          return;
        }

        try {
          const sanitizedCsv = convertToSanitizedCSV(results.data, headers);
          const blob = new Blob([sanitizedCsv], { type: 'text/csv' });
          const formData = new FormData();
          formData.append('file', blob, 'cleaned.csv');

          const token = getAuthToken();
          if (!token) {
            throw new Error('Authentication required. Please log in.');
          }
          
          const response = await axios.post(apiUrl, formData, {
            headers: { 
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            },
          });

          setMessage(response.data.message || 'Upload successful');
        } catch (err) {
          console.error('Upload error:', err);
          const errorMessage = err?.response?.data?.error || err?.message || 'Upload failed. Please check your file and try again.';
          setError(errorMessage);
        } finally {
          setUploading(false);
        }
      },
      error: (err) => {
        console.error('CSV parsing failed:', err);
        setError(`CSV parsing failed: ${err.message || 'Invalid file format. Please ensure your file is a valid CSV.'}`);
        setUploading(false);
      }
    });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Paper elevation={3} sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" mb={1} color="primary">
          Import Trades(CSV)
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Upload your trading data in CSV format. Maximum file size: 10MB
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {/* File Upload Section */}
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" mb={2}>File Upload</Typography>
                
                <Box
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  sx={{
                    border: '2px dashed',
                    borderColor: dragOver ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    bgcolor: dragOver ? 'primary.50' : 'background.default',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    mb: 2
                  }}
                >
                  <UploadFileIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                  <Typography variant="body1" mb={1}>
                    Drag & drop your CSV file here
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    or
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                  >
                    Choose File
                    <input
                      type="file"
                      accept=".csv"
                      hidden
                      onChange={(e) => handleFileSelect(e.target.files[0])}
                    />
                  </Button>
                </Box>

                {fileStats && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" mb={1}>Selected File:</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={fileStats.name} color="primary" variant="outlined" />
                      <Chip label={fileStats.size} size="small" />
                      <Tooltip title="Remove file">
                        <IconButton size="small" onClick={() => { setFile(null); setFileStats(null); setValidationResults(null); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Last modified: {fileStats.lastModified}
                    </Typography>
                  </Box>
                )}

                {uploading && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" mb={1}>Uploading...</Typography>
                    <LinearProgress />
                  </Box>
                )}

                {/* Strategy Selection */}
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Strategy (Optional)</InputLabel>
                    <Select
                      value={selectedStrategy}
                      label="Strategy (Optional)"
                      onChange={(e) => setSelectedStrategy(e.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {strategies.map((strategy) => (
                        <MenuItem key={strategy.strategyId} value={strategy.strategyId}>
                          {strategy.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Assign this strategy to all uploaded trades
                  </Typography>
                  {selectedStrategy && (
                    <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block', fontWeight: 'bold' }}>
                      ✓ Strategy will be applied to all {previewRows.length > 0 ? previewRows.length : ''} trades
                    </Typography>
                  )}
                </Box>

                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={!file || uploading || (csvHeaders.length > 0 && !validateMapping().isValid)}
                  fullWidth
                  size="large"
                >
                  {uploading ? 'Processing...' : 'Upload & Process'}
                </Button>
                
                {csvHeaders.length > 0 && !validateMapping().isValid && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Please map all required fields: {validateMapping().missingRequired.join(', ')}
                  </Alert>
                )}

                {message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            {/* Requirements & Validation */}
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" mb={2}>File Requirements</Typography>
                
                <Typography variant="subtitle2" color="error" mb={1}>Required Columns:</Typography>
                <List dense>
                  {REQUIRED_COLUMNS.map((col) => (
                    <ListItem key={col} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={col} />
                    </ListItem>
                  ))}
                </List>
                
                <Typography variant="subtitle2" color="info.main" mb={1} mt={2}>Optional Columns:</Typography>
                <List dense>
                  {OPTIONAL_COLUMNS.map((col) => (
                    <ListItem key={col} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <InfoIcon color="info" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={col} />
                    </ListItem>
                  ))}
                </List>

                {csvHeaders.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle2">
                        {validationResults ? 'Validation Results:' : 'Column Mapping:'}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<MappingIcon />}
                        onClick={() => setShowMappingModal(true)}
                      >
                        Map Columns
                      </Button>
                    </Stack>
                    
                    {validationResults ? (
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                          <Typography variant="body2">
                            {validationResults.validRows} / {validationResults.totalRows} valid rows
                          </Typography>
                        </Box>
                        
                        {validationResults.warnings.length > 0 && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            <Typography variant="body2" mb={1}>Warnings:</Typography>
                            {validationResults.warnings.slice(0, 3).map((warning, idx) => (
                              <Typography key={idx} variant="caption" display="block">
                                • {warning}
                              </Typography>
                            ))}
                            {validationResults.warnings.length > 3 && (
                              <Typography variant="caption" color="text.secondary">
                                ... and {validationResults.warnings.length - 3} more
                              </Typography>
                            )}
                          </Alert>
                        )}
                      </Stack>
                    ) : (
                      <Box>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                          Click "Map Columns" to customize field mapping
                        </Typography>
                        {!validateMapping().isValid && (
                          <Alert severity="error" size="small">
                            Missing required mappings: {validateMapping().missingRequired.join(', ')}
                          </Alert>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Data Preview & Analysis */}
        {previewRows.length > 0 && (
          <Box mt={4}>
            <Grid container spacing={3}>
              {/* Column Statistics */}
              {validationResults && (
                <Grid item xs={12} md={4}>
                  <Card elevation={1}>
                    <CardContent>
                      <Typography variant="h6" mb={2}>Column Analysis</Typography>
                      <Stack spacing={1}>
                        {Object.entries(validationResults.columnStats).slice(0, 6).map(([col, stats]) => (
                          <Box key={col} sx={{ p: 1, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="body2" fontWeight="medium">{col}</Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Typography variant="caption" color="success.main">
                                {stats.fillRate}% filled
                              </Typography>
                              {stats.emptyCount > 0 && (
                                <Typography variant="caption" color="warning.main">
                                  {stats.emptyCount} empty
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {/* Data Preview */}
              <Grid item xs={12} md={8}>
                <Card elevation={1}>
                  <CardContent>
                    <Typography variant="h6" mb={2}>
                      Data Preview (first 5 rows)
                    </Typography>
                    <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {previewHeaders.map((col, idx) => {
                              const isRequired = REQUIRED_COLUMNS.some(req => 
                                col.toLowerCase().includes(req.toLowerCase())
                              );
                              return (
                                <TableCell 
                                  key={idx} 
                                  sx={{ 
                                    whiteSpace: 'nowrap',
                                    bgcolor: isRequired ? 'error.50' : 'background.default',
                                    fontWeight: isRequired ? 'bold' : 'normal'
                                  }}
                                >
                                  {col}
                                  {isRequired && (
                                    <Chip 
                                      label="Required" 
                                      size="small" 
                                      color="error" 
                                      variant="outlined"
                                      sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
                                    />
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewRows.map((row, idx) => (
                            <TableRow key={idx} hover>
                              {previewHeaders.map((col, cidx) => {
                                const value = row[col];
                                const isEmpty = !value || value.toString().trim() === '';
                                return (
                                  <TableCell 
                                    key={cidx} 
                                    sx={{ 
                                      whiteSpace: 'nowrap',
                                      color: isEmpty ? 'text.disabled' : 'text.primary',
                                      fontStyle: isEmpty ? 'italic' : 'normal'
                                    }}
                                  >
                                    {isEmpty ? '(empty)' : value}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Duplicate Headers Modal */}
        <Modal
          open={showDuplicateModal}
          onClose={() => setShowDuplicateModal(false)}
        >
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper', p: 4, borderRadius: 2, boxShadow: 24, width: { xs: 300, sm: 500 }
          }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <WarningIcon color="warning" />
              <Typography variant="h6">
                Duplicate Headers Detected
              </Typography>
            </Stack>
            <Typography variant="body2" mb={2}>
              The following headers were duplicated and have been automatically renamed to ensure data integrity:
            </Typography>
            <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mb: 3 }}>
              {duplicateHeaders.map((dup, idx) => (
                <Chip 
                  key={idx} 
                  label={dup} 
                  color="warning" 
                  variant="outlined" 
                  sx={{ m: 0.5 }}
                />
              ))}
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Your data will be processed with the renamed headers. This won't affect the upload process.
            </Alert>
            <Button 
              onClick={() => setShowDuplicateModal(false)} 
              variant="contained" 
              fullWidth
              startIcon={<CheckCircleIcon />}
            >
              Continue
            </Button>
          </Box>
        </Modal>
        {/* Column Mapping Modal */}
        <Modal open={showMappingModal} onClose={() => setShowMappingModal(false)}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper', p: 4, borderRadius: 2, boxShadow: 24, 
            width: { xs: '90%', sm: 600 }, maxHeight: '80vh', overflow: 'auto'
          }}>
            <Typography variant="h6" mb={2}>Map CSV Columns</Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Map your CSV columns to the required fields:
            </Typography>
            
            <Grid container spacing={2}>
              {REQUIRED_COLUMNS.concat(OPTIONAL_COLUMNS).map((field) => {
                const isRequired = REQUIRED_COLUMNS.includes(field);
                const isMapped = columnMapping[field];
                return (
                  <Grid item xs={12} sm={6} key={field}>
                    <FormControl fullWidth size="small" error={isRequired && !isMapped}>
                      <InputLabel>{field} {isRequired && '*'}</InputLabel>
                      <Select
                        value={columnMapping[field] || ''}
                        label={`${field} ${isRequired ? '*' : ''}`}
                        onChange={(e) => setColumnMapping({...columnMapping, [field]: e.target.value})}
                      >
                        <MenuItem value="">None</MenuItem>
                        {csvHeaders.map((header) => (
                          <MenuItem key={header} value={header}>{header}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                );
              })}
            </Grid>
            
            {!validateMapping().isValid && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Please map all required fields to proceed
              </Alert>
            )}
            
            <Stack direction="row" spacing={2} mt={3}>
              <Button onClick={() => setShowMappingModal(false)} variant="outlined">
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  setShowMappingModal(false);
                  if (previewRows.length > 0) {
                    const validation = validateData(previewRows, csvHeaders);
                    setValidationResults(validation);
                  }
                }} 
                variant="contained"
                disabled={!validateMapping().isValid}
              >
                Apply Mapping
              </Button>
            </Stack>
          </Box>
        </Modal>

        {/* Help Section */}
        <Box mt={4}>
          <Card elevation={1} sx={{ bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'info.50' }}>
            <CardContent>
              <Typography variant="h6" mb={2} color="info.main">Tips for Better Data Quality</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText 
                        primary="Use consistent date formats" 
                        secondary="YYYY-MM-DD or DD/MM/YYYY"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText 
                        primary="Column mapping available" 
                        secondary="Auto-detects common column names"
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText 
                        primary="Flexible column names" 
                        secondary="'entry_price', 'buy_price' work too"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText 
                        primary="Manual column mapping" 
                        secondary="Map any column to required fields"
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </Paper>
    </Box>
  );
};

export default CSVUploadPage;
